import {
  GameType,
  HandHistoryResponse,
  HandHistorySnapshot,
  PotGroupPlayerSnapshot,
  PotGroupSnapshot,
  PotSnapshot
} from 'components/HandHistory/types';

export type HandHistoryDemoGameMode = 'cash' | 'tournament';
export type HandHistoryDemoVariant = 'texas' | 'plo' | 'plo8';

// Deterministic sample outcomes, not a poker engine or live history.
const now = Date.now();
export const handHistoryScenarioLabels: Record<string, string> = {};
// Economic inputs are kept separately from the public response so contract
// tests can compare our preview projection with the production snapshot builder.
export const handHistorySettlements: Record<string, PotSnapshot[]> = {};
let sequence = 0;

const player = (
  seat: number,
  name: string,
  cards: string[],
  extra: Partial<PotGroupPlayerSnapshot> = {}
): PotGroupPlayerSnapshot => ({
  playerId: `preview-${seat}`,
  seat,
  name,
  cards,
  cardsRevealed: cards.length > 0,
  net: 0,
  isDealer: seat === 1,
  ...extra
});

// Allocate contributions to each award, including each half of a Hi/Lo pot.
const award = (
  handType: 'Hi' | 'Lo',
  players: PotGroupPlayerSnapshot[],
  contributions: number[],
  winners: number[]
) => {
  const amount = contributions.reduce((sum, value) => sum + value, 0);
  return {
    handType,
    amount,
    players: players.map((entry, index) => {
      const grossWin = winners.includes(entry.seat) ? amount / winners.length : 0;
      return {
        ...entry,
        handType,
        isWinner: grossWin > 0,
        grossWin,
        net: grossWin - contributions[index]
      };
    })
  };
};

const pot = (
  name: string,
  players: PotGroupPlayerSnapshot[],
  contributions: number[],
  winners: number[]
): PotSnapshot => ({
  potId: name,
  name,
  potType: name === 'Main Pot' ? 'main' : 'side',
  amount: contributions.reduce((sum, value) => sum + value, 0),
  awards: [award('Hi', players, contributions, winners)]
});

// Table numbers shaped like production's (the panel header shows them). The
// gameId keeps the descriptive preview key: it is the hand/selection identity.
const PREVIEW_TABLE_IDS: Record<string, string> = {
  'cash-texas': '643692',
  'cash-plo': '643693',
  'cash-plo8': '643694',
  'tournament-texas': '715001',
  'tournament-plo': '715002',
  'tournament-plo8': '715003'
};

type SeatResults = (seat: number) => PotGroupPlayerSnapshot[];

const winnersOf = (p: PotSnapshot) => p.awards.flatMap((a) => a.players).filter((r) => r.isWinner);

const sumOf = (rows: PotGroupPlayerSnapshot[], pick: (row: PotGroupPlayerSnapshot) => number) =>
  rows.reduce((sum, row) => sum + pick(row), 0);

// One group per paid pot, as builder.js names each Hold'em winner against the
// pot they took. A row's net is its award less the matching share of what the
// seat put in, so a seat's rows still sum to its hand net.
const texasPotGroups = (pots: PotSnapshot[], results: SeatResults): PotGroupSnapshot[] =>
  pots
    .filter((p) => winnersOf(p).length > 0)
    .map((p) => ({
      name: p.name,
      potType: p.potType,
      amount: p.amount,
      players: winnersOf(p).map((row) => {
        const seatRows = results(row.seat);
        const gross = sumOf(seatRows, (r) => r.grossWin ?? 0);
        const committed = gross - sumOf(seatRows, (r) => r.net);
        const grossWin = row.grossWin ?? 0;
        return { ...row, grossWin, net: grossWin - committed * (grossWin / gross) };
      })
    }));

const noPayoutGroup = (
  players: PotGroupPlayerSnapshot[],
  results: SeatResults
): PotGroupSnapshot[] => {
  const losers = players.filter((p) => !results(p.seat).some((r) => r.isWinner));
  return losers.length === 0
    ? []
    : [
        {
          name: 'No Payout',
          potType: 'no_payout',
          amount: 0,
          players: losers.map((p) => ({
            ...p,
            grossWin: 0,
            net: sumOf(results(p.seat), (r) => r.net)
          }))
        }
      ];
};

type Action = [string, number, string[]?];
const makeHand = (
  label: string,
  variant: HandHistoryDemoVariant,
  gameType: GameType,
  board: string[],
  pots: PotSnapshot[],
  actions: Action[],
  options: { viewerSeat?: number; bustedSeat?: number } = {}
): HandHistorySnapshot => {
  const gameNo = ++sequence;
  const handId = `preview-${gameType}-${variant}-${gameNo}`;
  handHistoryScenarioLabels[handId] = label;
  handHistorySettlements[handId] = pots;
  const completedAt = now - (30 - gameNo) * 120000;
  const players = Array.from(
    new Map(
      pots.flatMap((p) => p.awards.flatMap((a) => a.players)).map((p) => [p.seat, p])
    ).values()
  );
  const results = (seat: number) =>
    pots.flatMap((p) => p.awards.flatMap((a) => a.players)).filter((p) => p.seat === seat);
  const viewerSeat = options.viewerSeat ?? 1;
  const net = results(viewerSeat).reduce((sum, p) => sum + p.net, 0);
  return {
    schemaVersion: 1,
    handId,
    gameNo,
    gameId: `preview-${gameType}-${variant}`,
    // A table number shaped like the real ones: the panel header shows it.
    tableId: PREVIEW_TABLE_IDS[`${gameType}-${variant}`],
    gameType,
    gameVariant: variant === 'texas' ? 'texas' : 'omaha',
    bettingStructure: variant === 'texas' ? 'NL' : variant === 'plo' ? 'PL' : 'HiLoPL',
    completedAt,
    startedAt: completedAt - 90000,
    playByPlayComplete: true,
    metadata:
      gameType === 'cash'
        ? { smallBlind: 1, bigBlind: 2 }
        : { smallBlind: 50, bigBlind: 100, level: 6, ante: 0 },
    board,
    playByPlay: actions.map(([text, totalPot, tableCards], index) => ({
      id: `${handId}-${index}`,
      order: index + 1,
      text,
      totalPot,
      tableCards,
      ts: completedAt - (actions.length - index) * 2000,
      type: tableCards ? 'street' : /posts/.test(text) ? 'blind' : 'action'
    })),
    tableSnapshot: {
      totalSeats: 6,
      viewerSeat,
      dealerSeat: 1,
      board,
      seats: players.map((p) => ({
        ...p,
        net: results(p.seat).reduce((sum, result) => sum + result.net, 0),
        isWinner: results(p.seat).some((result) => result.isWinner),
        stack:
          options.bustedSeat === p.seat
            ? 0
            : (gameType === 'cash' ? 100 : 5000) +
              results(p.seat).reduce((sum, result) => sum + result.net, 0)
      }))
    },
    ...(variant !== 'texas' ? { pots } : {}),
    // builder.js emits winner-only groups for Texas - one per pot when the hand
    // paid more than one pot - and contributor-level structured awards for
    // Omaha. Keep those paths distinct.
    potGroups:
      variant === 'texas' && pots.filter((p) => winnersOf(p).length > 0).length > 1
        ? [...texasPotGroups(pots, results), ...noPayoutGroup(players, results)]
        : variant === 'texas'
        ? [
            {
              name: 'Main Pot',
              potType: 'main',
              amount: pots.reduce((sum, p) => sum + p.amount, 0),
              players: players
                .filter((p) => results(p.seat).some((r) => r.isWinner))
                .map((p) => ({
                  ...p,
                  isWinner: true,
                  grossWin: results(p.seat).reduce((sum, r) => sum + (r.grossWin ?? 0), 0),
                  net: results(p.seat).reduce((sum, r) => sum + r.net, 0)
                }))
            },
            ...noPayoutGroup(players, results)
          ]
        : pots.flatMap((p) =>
            p.awards.map((a) => ({
              name: p.awards.length > 1 ? `${p.name} - ${a.handType}` : p.name,
              amount: a.amount,
              potType:
                p.awards.length > 1
                  ? a.handType === 'Hi'
                    ? ('hi' as const)
                    : ('lo' as const)
                  : p.potType,
              players: a.players
            }))
          ),
    ...(variant === 'plo8'
      ? {
          viewerResult: {
            seat: viewerSeat,
            net,
            outcome:
              net > 0 ? ('won' as const) : net < 0 ? ('lost' as const) : ('break_even' as const),
            scoop: pots.every(
              (p) =>
                p.awards.length === 2 &&
                p.awards.every(
                  (a) =>
                    a.players.filter((p) => p.isWinner).length === 1 &&
                    a.players.some((p) => p.seat === viewerSeat && p.isWinner)
                )
            )
          }
        }
      : {})
  };
};

// Limped pots followed by legal pot-limit bets: everyone contributes $20.
const playedBoard = (board: string[], names: string[]): Action[] => {
  const three = names.length === 3;
  const actions: Action[] = three
    ? [
        [`${names[1]} posts small blind $1`, 1],
        [`${names[2]} posts big blind $2`, 3],
        [`${names[0]} calls $2`, 5],
        [`${names[1]} calls $1`, 6],
        [`${names[2]} checks`, 6]
      ]
    : [
        [`${names[0]} posts small blind $1`, 1],
        [`${names[1]} posts big blind $2`, 3],
        [`${names[0]} calls $1`, 4],
        [`${names[1]} checks`, 4]
      ];
  let total = three ? 6 : 4;
  const order = three ? [names[1], names[2], names[0]] : [names[1], names[0]];
  [3, 4, 5].forEach((count, street) => {
    actions.push([`Dealing ${['Flop', 'Turn', 'River'][street]}`, total, board.slice(0, count)]);
    const bet = three ? [6, 12, 0][street] : [4, 12, 2][street];
    order.forEach((name, index) => {
      total += bet;
      actions.push([
        bet ? `${name} ${index === 0 ? 'bets' : 'calls'} $${bet}` : `${name} checks`,
        total
      ]);
    });
  });
  return actions;
};

const cashBoard = ['Ah', '7c', '2d', '9s', '3h'];
const cashPlayers = [
  player(1, 'Halden_T', ['As', 'Ad'], {
    showdown: true,
    handRank: 'Three of a Kind, Aces',
    winningCards: ['As', 'Ad', 'Ah', '9s', '7c']
  }),
  player(2, 'Bishop', [], { showdown: true, mucked: true })
];
const cashShowdown = makeHand(
  'Showdown · opponent mucks',
  'texas',
  'cash',
  cashBoard,
  [pot('Main Pot', cashPlayers, [20, 20], [1])],
  [
    ...playedBoard(cashBoard, ['Halden_T', 'Bishop']),
    ['Halden_T shows three aces; Bishop mucks; Halden_T wins $40', 40]
  ]
);

// Demo #2 (2026-09-24, HH-01): a hand that reaches showdown records every
// participant's hole cards, mucked included. The table keeps the muck; the
// payout row shows the caller's cards with the MUCK marker. Today's capture
// empties `cards` for a mucked seat (shared/handHistorySnapshot/builder.js,
// buildSeats), so this is the target document shape, not what production emits.
const calledAndMuckedPlayers = [
  player(1, 'Halden_T', ['As', 'Ad'], {
    showdown: true,
    handRank: 'Three of a Kind, Aces',
    winningCards: ['As', 'Ad', 'Ah', '9s', '7c']
  }),
  player(2, 'Bishop', ['Kd', 'Kc'], { showdown: true, mucked: true, handRank: 'Pair, Kings' })
];
const calledAndMucked = makeHand(
  'Showdown · caller mucks, history shows the cards',
  'texas',
  'cash',
  cashBoard,
  [pot('Main Pot', calledAndMuckedPlayers, [20, 20], [1])],
  [
    ...playedBoard(cashBoard, ['Halden_T', 'Bishop']),
    ['Halden_T shows three aces; Bishop mucks; Halden_T wins $40', 40]
  ]
);

const foldHand = (variant: 'texas' | 'plo', show: boolean) => {
  const board = show ? ['Kc', '7d', '2s'] : [];
  const players = [
    // The preview represents the viewer's already-recovered private overlay.
    // These invented cards never come from the live own-card endpoint.
    player(1, 'Halden_T', variant === 'plo' ? ['Ah', 'Qh', 'Jc', '10c'] : ['Ah', 'Qh'], {
      folded: true,
      badge: 'FOLD'
    }),
    player(
      2,
      'Bishop',
      show ? (variant === 'plo' ? ['Kh', 'Kd', 'Qd', 'Js'] : ['Kh', 'Kd']) : [],
      show
        ? {
            handRank: 'Three of a Kind, Kings',
            winningCards: ['Kh', 'Kd', 'Kc', '7d', '2s']
          }
        : {}
    )
  ];
  return makeHand(
    show ? 'Flop fold · winner shows voluntarily' : 'Pre-flop fold · winner keeps cards hidden',
    variant,
    'cash',
    board,
    [pot('Main Pot', players, show ? [2, 2] : [1, 1], [2])],
    show
      ? [
          ['Halden_T posts small blind $1', 1],
          ['Bishop posts big blind $2', 3],
          ['Halden_T calls $1', 4],
          ['Bishop checks', 4],
          ['Dealing Flop', 4, board],
          ['Bishop bets $4', 8],
          ['Halden_T folds', 8],
          ['Uncalled $4 returned to Bishop', 4],
          ['Bishop shows three kings and wins $4', 4]
        ]
      : [
          ['Halden_T posts small blind $1', 1],
          ['Bishop posts big blind $2', 3],
          ['Halden_T folds', 3],
          ['Uncalled $1 returned to Bishop', 2],
          ['Bishop wins $2 without showing', 2]
        ]
  );
};

const tieBoard = ['Ah', 'Kd', 'Qc', 'Js', '10h'];
const tiePlayers = [
  player(1, 'Halden_T', ['2c', '2d'], {
    showdown: true,
    handRank: 'Broadway Straight',
    winningCards: tieBoard
  }),
  player(2, 'Bishop', ['3c', '3d'], {
    showdown: true,
    handRank: 'Broadway Straight',
    winningCards: tieBoard
  })
];
const splitCash = makeHand(
  'Split pot · two winners, one folded player',
  'texas',
  'cash',
  tieBoard,
  [
    pot(
      'Main Pot',
      [...tiePlayers, player(3, 'Nyx', [], { folded: true, badge: 'FOLD' })],
      [22, 22, 20],
      [1, 2]
    )
  ],
  [
    ...playedBoard(tieBoard, ['Halden_T', 'Bishop', 'Nyx']).slice(0, -4),
    ['Dealing River', 60, tieBoard],
    ['Bishop bets $2', 62],
    ['Nyx folds', 62],
    ['Halden_T calls $2', 64],
    ['Both remaining players play the board; each wins $32', 64]
  ]
);

// Run it twice: both players all in pre-flop, one board each. `board` is run 1
// (every single-board consumer keeps working); `boards` carries both runouts,
// and each winning row says which run paid it (`run`). The capture does not
// emit either yet (EN-10948) - this is the design target.
const ritBoard1 = ['Kc', '7d', '2s', '9h', '3c'];
const ritBoard2 = ['Qs', '8d', '4c', '6s', 'Jd'];
const ritPlayers = [
  player(1, 'Halden_T', ['Ah', 'Kh'], {
    showdown: true,
    handRank: 'Pair of Kings',
    winningCards: ['Ah', 'Kh', 'Kc', '9h', '7d'],
    run: 1
  }),
  player(2, 'Bishop', ['Qc', 'Qd'], {
    showdown: true,
    handRank: 'Three of a Kind, Queens',
    winningCards: ['Qc', 'Qd', 'Qs', 'Jd', '8d'],
    run: 2
  }),
  player(3, 'Nyx', [], { folded: true, badge: 'FOLD' })
];
const runItTwice = makeHand(
  'Run it twice · one board each',
  'texas',
  'cash',
  ritBoard1,
  [pot('Main Pot', ritPlayers, [22, 22, 20], [1, 2])],
  [
    ['Halden_T posts small blind $1', 1],
    ['Bishop posts big blind $2', 3],
    ['Nyx raises to $20', 23],
    ['Halden_T goes all in for $22', 44],
    ['Bishop calls $22', 64],
    ['Nyx folds', 64],
    ['Halden_T and Bishop agree to run it twice', 64],
    ['Dealing run 1', 64, ritBoard1],
    ['Dealing run 2', 64, ritBoard2],
    [
      'Halden_T wins run 1 $32 with a pair of Kings; Bishop wins run 2 $32 with three of a kind, Queens',
      64
    ]
  ]
);
runItTwice.boards = [ritBoard1, ritBoard2];

const tournamentBoard = ['Ac', '7d', '4h', '2s', '9c'];
const tournamentPlayers = [
  player(1, 'Sol', ['As', 'Ad'], {
    showdown: true,
    handRank: 'Three of a Kind, Aces',
    winningCards: ['As', 'Ad', 'Ac', '9c', '7d']
  }),
  player(2, 'Rune', ['Kh', 'Kd'], {
    showdown: true,
    handRank: 'Pair of Kings',
    winningCards: ['Kh', 'Kd', 'Ac', '9c', '7d']
  }),
  player(3, 'Mara', ['Qh', 'Qd'], {
    showdown: true,
    handRank: 'Pair of Queens'
  })
];
const tournamentSidePot = makeHand(
  'Tournament all-in · main and side pot',
  'texas',
  'tournament',
  tournamentBoard,
  [
    pot('Main Pot', tournamentPlayers, [1000, 1000, 1000], [1]),
    pot('Side Pot 1', tournamentPlayers.slice(1), [1000, 1000], [2])
  ],
  [
    ['Rune posts small blind 50', 50],
    ['Mara posts big blind 100', 150],
    ['Sol moves all in for 1,000', 1150],
    ['Rune raises to 2,000 and is all in', 3100],
    ['Mara calls 1,900 and is all in', 5000],
    ['Dealing Flop', 5000, tournamentBoard.slice(0, 3)],
    ['Dealing Turn', 5000, tournamentBoard.slice(0, 4)],
    ['Dealing River', 5000, tournamentBoard],
    ['Sol wins main pot 3,000; Rune wins side pot 2,000; Mara is eliminated', 5000]
  ],
  { bustedSeat: 3 }
);
// All-in players finish with their gross awards, not a fictional starting stack.
tournamentSidePot.tableSnapshot.seats.forEach((seat) => {
  seat.stack = seat.seat === 1 ? 3000 : seat.seat === 2 ? 2000 : 0;
});
const tournamentSplit = makeHand(
  'Split pot · tournament chips',
  'texas',
  'tournament',
  tieBoard,
  [pot('Main Pot', tiePlayers, [1000, 1000], [1, 2])],
  [
    ['Halden_T posts small blind 50', 50],
    ['Bishop posts big blind 100', 150],
    ['Halden_T moves all in for 1,000', 1100],
    ['Bishop calls 900', 2000],
    ['Dealing Flop', 2000, tieBoard.slice(0, 3)],
    ['Dealing Turn', 2000, tieBoard.slice(0, 4)],
    ['Dealing River', 2000, tieBoard],
    ['Both players play the board; each wins 1,000', 2000]
  ]
);
tournamentSplit.tableSnapshot.seats[0].stack = 1000;

const ploBoard = ['Kc', '9d', '9s', '2h', '3c'];
const ploPlayers = [
  player(1, 'Ocho', ['Kd', 'Kh', 'Qc', 'Qd'], {
    showdown: true,
    handRank: 'Full House, Kings full of Nines',
    winningCards: ['Kd', 'Kh', 'Kc', '9d', '9s']
  }),
  player(2, 'Vela', ['As', 'Ad', 'Jh', '10c'], {
    showdown: true,
    handRank: 'Two Pair, Aces and Nines',
    winningCards: ['As', 'Ad', '9d', '9s', 'Kc']
  })
];
const ploShowdown = makeHand(
  'PLO showdown · exactly two hole cards',
  'plo',
  'cash',
  ploBoard,
  [pot('Main Pot', ploPlayers, [20, 20], [1])],
  [...playedBoard(ploBoard, ['Ocho', 'Vela']), ['Ocho wins $40 with a full house', 40]]
);

const hiloBoard = ['2c', '3d', '8h', 'Ks', 'Qc'];
const hiloPlayers = [
  player(1, 'Ocho', ['As', '4s', '10d', '9d'], {
    showdown: true,
    handRank: 'Ace High',
    loLabel: '8-4-3-2-A low'
  }),
  player(2, 'Vela', ['Kc', 'Kd', 'Jh', 'Jd'], {
    showdown: true,
    handRank: 'Three of a Kind, Kings',
    winningCards: ['Kc', 'Kd', 'Ks', 'Qc', '8h']
  }),
  player(3, 'Pike', ['Jc', 'Js', '7s', '6s'], {
    showdown: true,
    handRank: 'Pair of Jacks',
    loLabel: '8-7-6-3-2 low'
  })
];
const hiloPot: PotSnapshot = {
  potId: 'main',
  name: 'Main Pot',
  potType: 'main',
  amount: 60,
  awards: [
    award('Hi', hiloPlayers, [10, 10, 10], [2]),
    award(
      'Lo',
      hiloPlayers.map((p) => ({
        ...p,
        handRank: undefined,
        winningCards: p.seat === 1 ? ['As', '4s', '2c', '3d', '8h'] : undefined
      })),
      [10, 10, 10],
      [1]
    )
  ]
};
const hiloSplit = makeHand(
  'PLO8 · different High and Low winners',
  'plo8',
  'cash',
  hiloBoard,
  [hiloPot],
  [
    ...playedBoard(hiloBoard, ['Ocho', 'Vela', 'Pike']),
    ['Vela wins High $30; Ocho wins Low $30', 60]
  ]
);

const scoopPlayers = [
  player(1, 'Ocho', ['As', '4s', 'Kc', 'Kd'], {
    showdown: true,
    handRank: 'Three of a Kind, Kings',
    loLabel: '8-4-3-2-A low',
    winningCards: ['Kc', 'Kd', 'Ks', 'Qc', '8h']
  }),
  player(2, 'Vela', ['Qh', 'Qd', '7s', '6s'], {
    showdown: true,
    handRank: 'Three of a Kind, Queens',
    loLabel: '8-7-6-3-2 low'
  })
];
const scoopPot: PotSnapshot = {
  potId: 'main',
  name: 'Main Pot',
  potType: 'main',
  amount: 40,
  awards: [
    award('Hi', scoopPlayers, [10, 10], [1]),
    award(
      'Lo',
      scoopPlayers.map((p) => ({
        ...p,
        handRank: undefined,
        winningCards: p.seat === 1 ? ['As', '4s', '2c', '3d', '8h'] : undefined
      })),
      [10, 10],
      [1]
    )
  ]
};
const scoop = makeHand(
  'PLO8 · scoop High and Low',
  'plo8',
  'cash',
  hiloBoard,
  [scoopPot],
  [...playedBoard(hiloBoard, ['Ocho', 'Vela']), ['Ocho scoops High $20 and Low $20', 40]]
);

const noLowBoard = ['Kc', 'Kd', '9h', 'Qs', '2c'];
const noLowPlayers = [
  player(1, 'Ocho', ['Kh', '9s', 'Ac', '4d'], {
    showdown: true,
    handRank: 'Full House, Kings full of Nines',
    winningCards: ['Kh', '9s', 'Kc', 'Kd', '9h']
  }),
  player(2, 'Vela', ['As', 'Ad', 'Jh', '10c'], {
    showdown: true,
    handRank: 'King-high Straight',
    winningCards: ['Jh', '10c', 'Kc', 'Qs', '9h']
  })
];
const noLow = makeHand(
  'PLO8 · no qualifying Low',
  'plo8',
  'cash',
  noLowBoard,
  [{ ...pot('Main Pot', noLowPlayers, [20, 20], [1]), noLowQualified: true }],
  [...playedBoard(noLowBoard, ['Ocho', 'Vela']), ['No Low qualified; Ocho wins High $40', 40]]
);

const response = (hands: HandHistorySnapshot[]): HandHistoryResponse => {
  const sample = hands[0];
  const gameNo = Math.max(...hands.map((h) => h.gameNo)) + 1;
  return {
    locked: false,
    unlockedFromHandId: sample.handId,
    completedHands: hands,
    activeHand: {
      handId: `active-${sample.gameId}`,
      gameNo,
      gameType: sample.gameType,
      gameVariant: sample.gameVariant,
      bettingStructure: sample.bettingStructure,
      metadata: sample.metadata,
      board: sample.board.slice(0, 3),
      playByPlay: sample.playByPlay
        .slice(0, sample.playByPlay.findIndex((e) => e.tableCards?.length === 3) + 1)
        .map((event, index) => ({ ...event, id: `active-${index}`, ts: now - (20 - index) * 2000 }))
    }
  };
};

export const lockedHandHistoryFixture: HandHistoryResponse = {
  locked: true,
  lockedReason: 'NOT_DEALT_IN',
  completedHands: []
};
export const handHistoryScenarios: Record<
  HandHistoryDemoGameMode,
  Record<HandHistoryDemoVariant, HandHistoryResponse>
> = {
  cash: {
    texas: response([
      cashShowdown,
      foldHand('texas', true),
      foldHand('texas', false),
      splitCash,
      runItTwice,
      // Last so the index-based tests above keep their hands.
      calledAndMucked
    ]),
    plo: response([ploShowdown, foldHand('plo', true)]),
    plo8: response([hiloSplit, scoop, noLow])
  },
  tournament: {
    texas: response([tournamentSidePot, tournamentSplit]),
    // Omaha tournament controls are intentionally disabled in this preview.
    plo: response([ploShowdown]),
    plo8: response([hiloSplit])
  }
};

export const handHistoryFixture = handHistoryScenarios.cash.texas;
